import { AlertTriangle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { getMaintenanceStatus, setMaintenanceMode } from '@/api/maintenance-api';
import { useAuth } from '@/auth/useAuth';
import { useCompanyContextStore } from '@/store/company-context-store';
import { toast } from '@/store/toast-store';

export function MaintenanceBanner() {
  const { permissions } = useAuth();
  const queryClient = useQueryClient();
  const companyId = useCompanyContextStore((s) => s.activeCompanyId ?? s.companyId);

  const { data: maintenance } = useQuery({
    queryKey: ['maintenance-status', companyId],
    queryFn: getMaintenanceStatus,
    enabled: !!companyId,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const disableMutation = useMutation({
    mutationFn: () => setMaintenanceMode(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-status'] });
      toast('Maintenance mode disabled', { variant: 'success' });
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : 'Failed to disable maintenance mode', { variant: 'error' });
    },
  });

  if (!maintenance?.maintenanceMode) return null;

  return (
    <div className="flex items-center justify-between gap-4 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2">
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium text-amber-400">Maintenance Mode Active</span>
          {maintenance.maintenanceReason && (
            <span className="text-amber-400/70">
              &mdash; {maintenance.maintenanceReason}
            </span>
          )}
          {(maintenance.maintenanceStartedBy || maintenance.maintenanceStartedAt) && (
            <span className="text-amber-400/50">
              (
              {maintenance.maintenanceStartedBy && `by ${maintenance.maintenanceStartedBy}`}
              {maintenance.maintenanceStartedBy && maintenance.maintenanceStartedAt && ' '}
              {maintenance.maintenanceStartedAt && `at ${new Date(maintenance.maintenanceStartedAt).toLocaleString()}`}
              )
            </span>
          )}
        </div>
      </div>
      {permissions.canManageSystem && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => disableMutation.mutate()}
          disabled={disableMutation.isPending}
          className="shrink-0 border-amber-500/50 text-amber-400 hover:border-amber-400 hover:text-amber-300"
        >
          Disable
        </Button>
      )}
    </div>
  );
}
